// Customer types
export type CustomerStatus = "hot" | "warm" | "cold";
export type LeadSource = "walk-in" | "phone" | "website" | "social" | "referral" | "other";

export interface Customer {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  source: LeadSource;
  status: CustomerStatus;
  vehicle_interest: string | null;
  budget_min: number | null;
  budget_max: number | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

// Vehicle types
export type VehicleStatus = "available" | "sold" | "reserved" | "pending";

export interface VehicleImage {
  id: number;
  vehicle_id: number;
  image_url: string;
  sort_order: number;
  is_primary: boolean;
  created_at: string;
}

export interface Vehicle {
  id: number;
  make: string;
  model: string;
  year: number;
  vin: string | null;
  mileage: number | null;
  price: number;
  color: string | null;
  status: VehicleStatus;
  description: string | null;
  image_url: string | null;
  images?: VehicleImage[];
  created_at: string;
  updated_at: string;
}

// Interaction types
export type InteractionType = "note" | "call" | "email" | "meeting" | "other";

export interface Interaction {
  id: number;
  customer_id: number;
  type: InteractionType;
  content: string;
  created_at: string;
}

// Task types
export type TaskPriority = "urgent" | "normal" | "low";

export interface Task {
  id: number;
  customer_id: number | null;
  title: string;
  description: string | null;
  due_date: string;
  priority: TaskPriority;
  completed: boolean;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  customer?: Customer;
}

// Deal types
export type DealStage =
  | "new_lead"
  | "contacted"
  | "test_drive"
  | "negotiation"
  | "closed_won"
  | "closed_lost";

export interface Deal {
  id: number;
  customer_id: number;
  vehicle_id: number | null;
  stage: DealStage;
  value: number | null;
  notes: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
  customer?: Customer;
  vehicle?: Vehicle;
}

// Dashboard types
export interface DashboardStats {
  totalLeads: number;
  hotLeads: number;
  pipelineValue: number;
  totalSales: number;
  tasksDueToday: number;
  recentLeads: Customer[];
  upcomingTasks: Task[];
  dealsByStage: Record<DealStage, number>;
}

// Utility function to get initials from name
export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// Format currency (Thai Baht)
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    maximumFractionDigits: 0,
  }).format(amount);
}

// Format date (Thai locale)
export function formatDate(date: string | Date): string {
  return new Intl.DateTimeFormat("th-TH", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(date));
}

// Format relative date
export function formatRelativeDate(date: string | Date): string {
  const now = new Date();
  const then = new Date(date);
  const diffInMs = then.getTime() - now.getTime();
  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

  if (diffInDays === 0) return "วันนี้";
  if (diffInDays === 1) return "พรุ่งนี้";
  if (diffInDays === -1) return "เมื่อวาน";
  if (diffInDays > 0 && diffInDays <= 7) return `อีก ${diffInDays} วัน`;
  if (diffInDays < 0 && diffInDays >= -7) return `${Math.abs(diffInDays)} วันที่แล้ว`;

  return formatDate(date);
}

// Status badge variant mapping
export function getStatusBadgeVariant(status: CustomerStatus): "hot" | "warm" | "cold" {
  return status;
}

// Stage badge variant mapping
export function getStageBadgeVariant(stage: DealStage): string {
  const mapping: Record<DealStage, string> = {
    new_lead: "stage-new",
    contacted: "stage-contacted",
    test_drive: "stage-test-drive",
    negotiation: "stage-negotiation",
    closed_won: "stage-won",
    closed_lost: "stage-lost",
  };
  return mapping[stage];
}

// Stage display names
export function getStageDisplayName(stage: DealStage): string {
  const mapping: Record<DealStage, string> = {
    new_lead: "New Lead",
    contacted: "Contacted",
    test_drive: "Test Drive",
    negotiation: "Negotiation",
    closed_won: "Closed Won",
    closed_lost: "Closed Lost",
  };
  return mapping[stage];
}

// Source display names
export function getSourceDisplayName(source: LeadSource): string {
  const mapping: Record<LeadSource, string> = {
    "walk-in": "Walk-in",
    phone: "Phone",
    website: "Website",
    social: "Social Media",
    referral: "Referral",
    other: "Other",
  };
  return mapping[source];
}

// Priority badge variant mapping
export function getPriorityBadgeVariant(
  priority: TaskPriority
): "priority-urgent" | "priority-normal" | "priority-low" {
  return `priority-${priority}` as "priority-urgent" | "priority-normal" | "priority-low";
}
